import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSelectModule } from '@angular/material/select';
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { ENTER } from '@angular/cdk/keycodes';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { UtilsService } from '../../../../shared/services/utils.service';
import { Constants } from '../../../../shared/interfaces/constants';
import { MatDividerModule } from '@angular/material/divider';
import { Subject } from 'rxjs/internal/Subject';
import {
	ManagementCategoryClippingControllerService,
	ManagementCategoryResponse,
	ManagementKeywordControllerService,
	ManagementKeywordResponse,
	ManagementKeywordsRequest,
	ManagementTagClippingControllerService,
	ManagementTagResponse,
	TagResponseItem,
} from 'api-clipping';
import { SharedService } from '../../../../shared/services/shared.service';
import { take } from 'rxjs/internal/operators/take';
import { MatExpansionModule } from '@angular/material/expansion';

interface ManagementCategoryWithTags extends ManagementCategoryResponse {
	tags?: ManagementTagWithAdditionalInfo[];
	isOpened?: boolean;
}

interface ManagementTagWithAdditionalInfo extends ManagementTagResponse {
	checked?: boolean;
}
interface InputData {
	mode: 'NEW' | 'EDIT' | 'DELETE' | 'PENDING';
	type: string;
	item: ManagementKeywordResponse;
}

@Component({
	selector: 'app-keyword',
	standalone: true,
	imports: [
		FormsModule,
		ReactiveFormsModule,
		TranslateModule,
		MatFormFieldModule,
		MatInputModule,
		MatIconModule,
		MatCardModule,
		MatButtonModule,
		MatTooltipModule,
		MatSelectModule,
		MatChipsModule,
		TranslateModule,
		MatCheckboxModule,
		MatProgressSpinnerModule,
		MatDividerModule,
		MatExpansionModule,
		MatDialogModule,
	],
	templateUrl: './keyword.component.html',
	styleUrl: './keyword.component.scss',
})
export class KeywordComponent implements OnInit, OnDestroy {
	private onDestroy: Subject<any> = new Subject();

	formGroup: FormGroup = new FormGroup({});
	keywordSources = Constants.KEYWORDS_SOURCE_TYPES;
	tagCategories: ManagementCategoryWithTags[] = [];
	selectedTags: TagResponseItem[] = [];
	selectedSources: string[] = [];
	texts: string[] = [];
	readonly separatorKeyCodes: number[] = [ENTER];

	apiLoading = {
		management: false,
	};

	constructor(
		public dialogRef: MatDialogRef<KeywordComponent>,
		@Inject(MAT_DIALOG_DATA) public data: InputData,
		private fb: FormBuilder,
		public utils: UtilsService,
		private apiClippingKeyword: ManagementKeywordControllerService,
		private apiClippingCategory: ManagementCategoryClippingControllerService,
		private apiClippingTag: ManagementTagClippingControllerService,
		private sharedService: SharedService,
		private translate: TranslateService
	) {
		switch (this.data.mode) {
			case 'NEW': {
				this.formGroup = this.fb.group({
					texts: [[], [Validators.required, Validators.minLength(1)]],
					sources: [[], [Validators.required, Validators.minLength(1)]],
					tags: [, [Validators.required]],
				});
				this.getCategories();
				break;
			}
			case 'EDIT': {
				this.formGroup = this.fb.group({
					text: [, [Validators.required]],
					sources: [[], [Validators.required, Validators.minLength(1)]],
					tags: [, [Validators.required]],
				});

				if (this.data.item) {
					this.formGroup.patchValue(this.data.item);
					this.selectedSources = this.data.item.sources ? [...this.data.item.sources] : [];
					this.formGroup.controls['sources'].setValue(this.selectedSources);
					this.selectedTags = this.data.item.tags || [];
          this.formGroup.controls['tags'].setValue(this.selectedTags.map(item => item.id));
				}
				this.getCategories();
				break;
			}
		}
	}

	ngOnInit(): void {}

	ngOnDestroy(): void {
		this.onDestroy.next('');
	}

	manageTags(event: MatCheckboxChange, tag: ManagementTagWithAdditionalInfo) {
		if (event && event.checked) {
			this.selectedTags.push(tag);
		} else {
			this.selectedTags = this.selectedTags.filter(item => item.id !== tag.id);
		}
		this.formGroup.controls['tags'].setValue(this.selectedTags.map(tag => tag.id));
	}

	isSourceDisabled(source: string): boolean {
		if (this.selectedSources.includes(source)) {
			return false;
		}
		return source === 'INOREADER' ? this.selectedSources.some(item => item !== 'INOREADER') : this.selectedSources.includes('INOREADER');
	}

	manageSources(event: MatCheckboxChange, source: string) {
		if (event && event.checked) {
			if (!this.selectedSources.includes(source)) {
				this.selectedSources.push(source);
			}
		} else {
			this.selectedSources = this.selectedSources.filter(item => item !== source);
		}
		this.formGroup.controls['sources'].setValue(this.selectedSources);
	}

	addText(event: MatChipInputEvent): void {
		const value = (event.value || '').trim();
		if (value && !this.texts.includes(value)) {
			this.texts.push(value);
			this.formGroup.controls['texts'].setValue(this.texts);
		}
		event.chipInput.clear();
	}

	addTextFromInputOnBlur(event: FocusEvent): void {
		const input = event.target as HTMLInputElement;
		const value = (input.value || '').trim();
		if (value && !this.texts.includes(value)) {
			this.texts.push(value);
			this.formGroup.controls['texts'].setValue(this.texts);
		}
		input.value = '';
	}

	removeText(text: string): void {
		this.texts = this.texts.filter(item => item !== text);
		this.formGroup.controls['texts'].setValue(this.texts);
	}

	action() {
    // Guards NEW/EDIT directly (not just the Save button's [disabled]) - Enter no longer submits
    // the form, but this keeps action() itself safe to call from anywhere without re-deriving the
    // same "at least one text, one source, one tag" rule the form's own validators already encode.
    if ((this.data.mode === 'NEW' || this.data.mode === 'EDIT') && this.formGroup.invalid) {
      return;
    }
    switch (this.data.mode) {
			case 'NEW': {
				this.createKeyword();
				break;
			}
			case 'EDIT': {
				this.editKeyword();
				break;
			}
			case 'DELETE': {
        this.deleteKeyword();
				break;
			}
		}
	}

	private getCategories() {
		this.apiClippingCategory
			.getCategories()
			.pipe(take(1))
			.subscribe({
				next: response => {
					this.tagCategories = response || [];

					if (this.data.mode === 'EDIT' && this.selectedTags.length) {
						const selectedCategoryIds = new Set(this.selectedTags.map(tag => tag.categoryId));
						(this.tagCategories as ManagementCategoryWithTags[]).forEach(category => {
							if (selectedCategoryIds.has(category.id)) {
								category.isOpened = true;
								this.getTagsForCategory(category);
							}
						});
					}
				},
				error: () => {
					this.tagCategories = [];
				},
			});
	}

	getTagsForCategory(category: ManagementCategoryWithTags) {
		if (category && !category.tags) {
			this.apiClippingTag
				.getTags(category.id)
				.pipe(take(1))
				.subscribe({
					next: response => {
						(this.tagCategories as ManagementCategoryWithTags[]).some(categoryItem => {
							if (categoryItem.id === category.id && response) {
								category.tags = [];
								response.forEach(tag => {
									category.tags?.push({ ...tag, checked: this.selectedTags.find(selectedTag => selectedTag.id === tag.id) ? true : false });
								});
								return true;
							}
							return false;
						});
					},
					error: () => {},
				});
		}
	}

  private createKeyword() {
    this.apiLoading.management = true;
    const request: ManagementKeywordsRequest = this.utils.validateRequestObject(this.formGroup.value);
    this.apiClippingKeyword
    .createKeywords(request)
    .pipe(take(1))
    .subscribe({
      next: () => {
        this.sharedService.showNotification.next({
          message: this.translate.instant('NOTIFICATIONS.RECORD_CREATED'),
          type: 'SUCCESS',
        });
        this.closeDialog();
      },
      error: err => {
        this.sharedService.showNotification.next({
          message: `${this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR')} \n ${err.error?.message || ''}`,
          type: 'ERROR',
        });
        this.apiLoading.management = false;
      },
    });
  }

  private editKeyword() {
    if (this.data.item && this.data.item.id) {
      this.apiLoading.management = true;
      this.apiClippingKeyword
        .updateKeyword(this.utils.validateRequestObject(this.formGroup.value), this.data.item.id)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.sharedService.showNotification.next({
              message: this.translate.instant('NOTIFICATIONS.RECORD_UPDATED'),
              type: 'SUCCESS',
            });
            this.closeDialog();
          },
          error: () => {
            this.sharedService.showNotification.next({
              message: this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR'),
              type: 'ERROR',
            });
            this.apiLoading.management = false;
          },
        });
    }
  }

  private deleteKeyword() {
    if (this.data.item && this.data.item.id) {
      this.apiLoading.management = true;
      this.apiClippingKeyword
        .deleteKeyword(this.data.item.id)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.sharedService.showNotification.next({
              message: this.translate.instant('NOTIFICATIONS.RECORD_DELETED'),
              type: 'SUCCESS',
            });
            this.closeDialog();
          },
          error: () => {
            this.sharedService.showNotification.next({
              message: this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR'),
              type: 'ERROR',
            });
            this.apiLoading.management = false;
          },
        });
    }
  }

	closeDialog() {
		this.dialogRef.close();
	}
}
